import { useEffect, useState } from 'react';
import { ButtonLink } from '../ui/Button';
import { Container } from '../ui/Container';
import { Icon } from '../ui/Icon';
import { Logo } from './Logo';
import { LocaleSwitcher } from './LocaleSwitcher';
import i18n from '../../i18n/config';
import { t } from '../../i18n/t';
import { localizedPath, stripLocalePrefix } from '../../i18n/path';

function financingUrl(): string {
  const locale = i18n.locale || 'lv';
  return locale === 'lv' ? 'https://kreditiem.lv/' : `https://kreditiem.lv/${locale}/`;
}

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const path = typeof window !== 'undefined' ? stripLocalePrefix(window.location.pathname) : '';
  const links = [
    { to: '/privatpersonam', label: t('ui.nav.private') },
    { to: '/uznemumiem', label: t('ui.nav.business') },
    { to: '/blogs', label: t('ui.nav.blog') },
    { to: '/kontakti', label: t('ui.nav.contacts') },
  ];

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('overflow-hidden');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen]);

  const linkClass = (to: string) =>
    `rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
      path === to || path.startsWith(`${to}/`)
        ? 'text-primary-600'
        : 'text-gray-600 hover:text-primary-600'
    }`;

  const navLinks = (
    <>
      {links.map((link) => (
        <a key={link.to} href={localizedPath(link.to)} className={linkClass(link.to)}>
          {link.label}
        </a>
      ))}
      <a
        href={financingUrl()}
        className="rounded-md px-3 py-2 text-sm font-bold text-gray-700 transition-colors duration-200 hover:text-primary-600"
      >
        {t('ui.nav.financing')}
      </a>
    </>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-white/90 backdrop-blur-md">
      <Container>
        {/* Mobile header */}
        <div className="flex items-center justify-between gap-4 py-3.5 sm:hidden">
          <Logo />
          <div className="flex items-center gap-2">
            <LocaleSwitcher compact />
            <button
              type="button"
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary-600"
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              aria-label={isOpen ? t('ui.nav.close') : t('ui.nav.open')}
              onClick={() => setIsOpen((open) => !open)}
            >
              <Icon name={isOpen ? 'x' : 'menu'} className="h-6 w-6" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Tablet + desktop: two rows below lg, single row at lg+ */}
        <div className="hidden flex-col gap-3 py-3.5 sm:flex sm:py-4 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center lg:gap-0">
          <div className="flex items-center justify-between lg:contents">
            <div className="lg:justify-self-start">
              <Logo />
            </div>
            <div className="flex items-center gap-4 lg:col-start-3 lg:justify-self-end">
              <LocaleSwitcher />
              <ButtonLink to="/pieteikums" variant="accent">
                {t('ui.nav.apply')}
              </ButtonLink>
            </div>
          </div>

          <nav
            className="flex w-full flex-wrap items-center justify-center gap-x-6 gap-y-2 lg:col-start-2 lg:row-start-1 lg:w-auto"
            aria-label={t('ui.nav.main')}
          >
            {navLinks}
          </nav>
        </div>
      </Container>

      {isOpen && (
        <div id="mobile-menu" className="border-t border-gray-200 bg-white sm:hidden">
          <Container>
            <nav className="flex flex-col gap-1 py-4" aria-label={t('ui.nav.mobile')}>
              <a
                href={financingUrl()}
                className="block rounded-md px-4 py-3 text-base font-bold text-gray-700 hover:text-primary-600"
                onClick={() => setIsOpen(false)}
              >
                {t('ui.nav.financing')}
              </a>
              {links.map((link) => (
                <a
                  key={link.to}
                  href={localizedPath(link.to)}
                  className={`${linkClass(link.to)} block px-4 py-3 text-base`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-3">
                <ButtonLink to="/pieteikums" variant="accent" fullWidth size="lg">
                  {t('ui.nav.apply')}
                </ButtonLink>
              </div>
            </nav>
          </Container>
        </div>
      )}
    </header>
  );
}
